<script setup lang="ts">
import { useProjectStore } from '@/stores/project'
import { useCalculationStore } from '@/stores/calculation'
import { storeToRefs } from 'pinia'
import {
  CreateOrUpdateDialog,
  DeleteDialog,
  DuplicateDialog,
  DataTableWrapper
} from '@haskoning/vue-component-library'
import { useAuthorizationStore } from '@/stores/authorization'
import { computed } from 'vue'
import type { Calculation } from '@/stores/type/calculation.type'
import CalculationTableRow from '@/components/calculation-index/CalculationTableRow.vue'
import ProjectParamsInfo from '@/components/calculation-index/ProjectParamsInfo.vue'

const projectStore = useProjectStore()
const calculationStore = useCalculationStore()
const authorizationStore = useAuthorizationStore()

const {
  currentItem: currentProjectItem,
  createUpdateDialog: projectCreateUpdateDialog,
  writeLoading: projectWriteLoading,
  fieldsFiltered: projectFieldsFiltered
} = storeToRefs(projectStore)
const {
  currentItem: currentCalculationItem,
  createUpdateDialog: calculationCreateUpdateDialog,
  fieldsFiltered: calculationFieldsFiltered,
  fetchLoading: calculationFetchLoading,
  items: calculationItems,
  selectedItems: calculationSelectedItems,
  deleteDialog,
  duplicateDialog,
  deleteLoading,
  writeLoading: calculationWriteLoading,
  canAddCalculation,
  canChangeCalculation,
  canDeleteCalculation,
  isApprovedProjectParams
} = storeToRefs(calculationStore)

const isCheckboxShown = computed(
  () => canAddCalculation.value || canChangeCalculation.value || canDeleteCalculation.value
)

const headers = computed(() => [
  { title: '', align: isCheckboxShown.value ? 'left' : ' d-none' },
  { title: 'Number', align: 'left' },
  { title: 'Calculation Name', align: 'left' },
  { title: 'Status', align: 'left' },
  { title: 'Cross Sections', align: 'center' },
  { title: 'Error File', align: 'center' },
  { title: 'Date Created', align: 'left' },
  { title: 'Date Modified', align: 'left' },
  { title: 'Favorite', align: 'center' },
  { title: 'Edit', align: isCheckboxShown.value ? 'center' : ' d-none' }
])

const filterItemsBySearch = <T extends Calculation>(
  items: T[],
  search: string,
  keys: (keyof T)[]
): T[] => {
  if (!search) return items
  return items.filter((item) =>
    keys.some((key) => item[key]?.toString().toLowerCase().includes(search.toLowerCase()))
  )
}

const getFilteredItems = (
  items: Calculation[],
  search: string,
  favorite: boolean
): Calculation[] => {
  const filteredItems = filterItemsBySearch(items, search, [
    'name',
    'location',
    'client',
    'description'
  ])
    .sort((a, b) => new Date(b.modified).getTime() - new Date(a.modified).getTime())
    .sort((a, b) => Number(b.favorite) - Number(a.favorite))

  return favorite ? filteredItems.filter((item) => item.favorite) : filteredItems
}

const canChangeProject = computed(() => {
  return authorizationStore.hasServicePermission({
    authSource: 'ProjectUser',
    allowed: ['change_project']
  })
})

const handleEditProject = () => {
  projectStore.openCreateUpdateDialog()
}

const addCalculation = () => {
  const newCalculation = calculationStore.createNewCalculation(currentProjectItem.value.id)
  calculationStore.setCurrentItem(newCalculation)
  calculationStore.openCreateUpdateDialog()
}
</script>

<template>
  <template v-if="calculationFetchLoading">
    <div class="d-flex justify-center align-center fill-height">
      <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
    </div>
  </template>
  <template v-else>
    <div class="px-8 py-6">
      <DataTableWrapper
        v-if="isApprovedProjectParams || calculationItems.length > 0"
        :title="currentProjectItem?.name || ''"
        add-title="Add Calculation"
        :headers="headers"
        :items="calculationItems"
        :selected-items="calculationSelectedItems"
        :get-filtered-items="getFilteredItems"
        :can-change-title="canChangeProject"
        :can-add-item="canAddCalculation"
        :can-change-item="canChangeCalculation"
        :can-delete-item="canDeleteCalculation"
        :show-view-favorites-button="true"
        :show-move-to-archive-button="false"
        :show-duplicate-button="true"
        :show-delete-button="true"
        @change-title="handleEditProject"
        @add-item="addCalculation"
        @update-selected-items="calculationStore.updateSelectedItems"
        @move-selected-items-to-archive="calculationStore.moveSelectedItemsToArchive"
        @duplicate-selected-items="calculationStore.duplicateSelectedItems"
        @delete-selected-items="calculationStore.deleteSelectedItems"
      >
        <template #item="{ item }">
          <CalculationTableRow :item="item as Calculation" :is-checkbox-shown="isCheckboxShown" />
        </template>
      </DataTableWrapper>

      <ProjectParamsInfo v-else />
      <CreateOrUpdateDialog
        v-if="currentProjectItem"
        name="project"
        v-model="projectCreateUpdateDialog"
        :item="currentProjectItem"
        :loading="projectWriteLoading"
        :fields="projectFieldsFiltered"
        @submit="projectStore.postOrUpdate($event)"
      />
      <CreateOrUpdateDialog
        v-if="currentCalculationItem"
        name="calculation"
        v-model="calculationCreateUpdateDialog"
        :item="currentCalculationItem"
        :loading="calculationWriteLoading"
        :fields="calculationFieldsFiltered"
        @submit="calculationStore.postOrUpdate($event)"
      />
      <DeleteDialog
        name="calculation"
        v-model="deleteDialog"
        :loading="deleteLoading"
        :bulk-delete="true"
        :items="calculationSelectedItems"
        @bulk-delete="calculationStore.bulkDelete($event)"
        @submitted-and-closed="calculationStore.clearSelectedItems"
      />
      <DuplicateDialog
        name="calculation"
        v-model="duplicateDialog"
        :loading="calculationWriteLoading"
        :bulk-duplicate="true"
        :items="calculationSelectedItems"
        @bulk-duplicate="calculationStore.bulkDuplicate($event)"
        @submitted-and-closed="calculationStore.clearSelectedItems"
      />
    </div>
  </template>
</template>
